import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, X, Loader2, MessageCircle } from 'lucide-react';
import PhoneInput from './PhoneInput';
import OTPInput from './OTPInput';

export default function PhoneStartModal({ open, onClose, intendedPlan, intendedFeature }) {
  const [step, setStep] = useState('phone');  // phone | otp | success
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [delivery, setDelivery] = useState(null);
  const [phoneMasked, setPhoneMasked] = useState('');
  const [devOtp, setDevOtp] = useState(null);
  const navigate = useNavigate();

  if (!open) return null;

  const reset = () => {
    setStep('phone');
    setPhone('');
    setOtp('');
    setError('');
    setLoading(false);
    setDelivery(null);
    setPhoneMasked('');
    setDevOtp(null);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const startFlow = async () => {
    if (!phone || phone.length < 8) {
      setError('Enter a valid phone number with country code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      // Referral attribution: ?ref= in URL (persisted so it survives navigation)
      let referralCode = null;
      try {
        const urlRef = new URLSearchParams(window.location.search).get('ref');
        if (urlRef) localStorage.setItem('ci_ref_code', urlRef);
        referralCode = urlRef || localStorage.getItem('ci_ref_code') || null;
      } catch (e) { /* storage unavailable */ }

      const r = await fetch('/api/v2/auth/phone/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          intended_plan: intendedPlan || null,
          intended_feature: intendedFeature || null,
          referral_code: referralCode,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        setError(e.detail || `Error ${r.status}`);
        setLoading(false);
        return;
      }
      const data = await r.json();
      setDelivery(data.delivery);
      setPhoneMasked(data.phone_masked);
      setDevOtp(data.dev_otp);
      setStep('otp');
      // Track analytics event
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({ event: 'phone_start_submit', intended_plan: intendedPlan });
      }
    } catch (e) {
      setError('Network error. Try again.');
    }
    setLoading(false);
  };

  const verifyOtp = async (codeFromComplete) => {
    const code = codeFromComplete || otp;
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const r = await fetch('/api/v2/auth/phone/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp: code }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        setError(e.detail || 'Verification failed');
        setLoading(false);
        return;
      }
      const data = await r.json();
      // Persist session token (production would set HttpOnly cookie server-side)
      if (data.session_token) {
        localStorage.setItem('ci_session_token', data.session_token);
        localStorage.setItem('ci_merchant_id', data.merchant_id || '');
      }
      setStep('success');
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({ event: 'phone_signup_complete', is_new_merchant: data.is_new_merchant });
      }
      setTimeout(() => {
        navigate(data.redirect_to || '/portfolio');
      }, 1200);
    } catch (e) {
      setError('Network error. Try again.');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={close}>
      <div
        className="bg-[#0D1628] border border-[#22304A] rounded-xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#3EF1C7]" />
            <h2 className="font-semibold text-white">Find your money — free</h2>
          </div>
          <button onClick={close} className="text-[#64748B] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'phone' && (
          <>
            <p className="text-sm text-[#94A3B8] mb-4">
              Agents scan your store for leaked margin, stale deals, and missed revenue — and text you what they find. Free onboarding is limited each week, so we'll save your spot in line (invite friends to move up, or a 14-day trial skips the queue). We'll send a 6-digit code via WhatsApp or SMS. No password, no email, no card.
            </p>
            <PhoneInput value={phone} onChange={setPhone} disabled={loading} autoFocus />
            {error && <div className="text-sm text-[#FF6B6B] mt-2">{error}</div>}
            <button
              onClick={startFlow}
              disabled={loading || !phone}
              className="mt-4 w-full px-4 py-3 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
              Send code
            </button>
            <p className="text-xs text-[#64748B] mt-4">
              By continuing you agree to our terms of service and privacy policy.
              You'll receive messages via WhatsApp or SMS — msg &amp; data rates may
              apply. Reply STOP to opt out anytime, SMS or WHATSAPP to pick your channel.
            </p>
          </>
        )}

        {step === 'otp' && (
          <>
            <p className="text-sm text-[#94A3B8] mb-1">
              We sent a 6-digit code to {phoneMasked || phone}.
            </p>
            {delivery?.primary_channel && (
              <p className="text-xs text-[#3EF1C7] mb-4">via {delivery.primary_channel}</p>
            )}
            {!delivery?.primary_channel && (
              <p className="text-xs text-[#FFA500] mb-4">
                {devOtp ? `Dev mode — your code: ${devOtp}` : 'Delivery may take up to a minute…'}
              </p>
            )}
            <OTPInput
              value={otp}
              onChange={setOtp}
              onComplete={verifyOtp}
              disabled={loading}
              autoFocus
            />
            {error && <div className="text-sm text-[#FF6B6B] mt-3 text-center">{error}</div>}
            <button
              onClick={() => verifyOtp()}
              disabled={loading || otp.length !== 6}
              className="mt-4 w-full px-4 py-3 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Verify
            </button>
            <button
              onClick={() => setStep('phone')}
              className="mt-2 w-full text-xs text-[#64748B] hover:text-white"
            >
              ← Use a different number
            </button>
          </>
        )}

        {step === 'success' && (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-[#3EF1C7]/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <MessageCircle className="w-6 h-6 text-[#3EF1C7]" />
            </div>
            <p className="font-semibold text-white">You're in. Taking you to your portfolio…</p>
          </div>
        )}
      </div>
    </div>
  );
}
